// LIMIT-07 — a rate limit's window and wait, in words, in the user's language.

type Unit = 'day' | 'hour' | 'minute' | 'second';
const SPANS: ReadonlyArray<readonly [Unit, number]> = [
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
  ['second', 1],
];

/**
 * formatDuration renders whole seconds in words for `locale` (LIMIT-07).
 * 'window': the largest unit that divides it exactly; exactly one unit reads as
 * the bare unit ("minute"), so copy can say "per {window}". 'wait': rounded UP to
 * one unit (≤90 s seconds, ≤90 min minutes, ≤36 h hours, else days) — a stated
 * wait may overstate, never understate.
 */
export function formatDuration(seconds: number, locale: string, mode: 'wait' | 'window'): string {
  const s = Math.max(1, Math.ceil(seconds));
  const [unit, n] = mode === 'window' ? exactUnit(s) : roundedUnit(s);
  try {
    const fmt = new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'long' });
    if (mode === 'window' && n === 1) {
      const name = fmt.formatToParts(1).find((p) => p.type === 'unit')?.value;
      if (name) return name;
    }
    return fmt.format(n);
  } catch {
    return `${n} ${unit}${n === 1 ? '' : 's'}`;
  }
}

function exactUnit(s: number): [Unit, number] {
  for (const [unit, size] of SPANS) if (s % size === 0) return [unit, s / size];
  return ['second', s];
}

// ceiling: Intl gives the nominative, so a de wait in days reads "in 3 Tage" (should be "Tagen"); every LIMIT-07 DEFAULT window is <= 1 h so the largest default wait is "60 Minuten", but an LVDR_RATELIMIT_* key can set a longer one; use a per-locale relative phrase if a window over 36 h appears
function roundedUnit(s: number): [Unit, number] {
  if (s <= 90) return ['second', s];
  if (s <= 90 * 60) return ['minute', Math.ceil(s / 60)];
  if (s <= 36 * 3_600) return ['hour', Math.ceil(s / 3_600)];
  return ['day', Math.ceil(s / 86_400)];
}
